// frontend/src/pages/WordPreview.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { templateApi } from '../services/templateApi';
import { toast } from '../store/toastStore';
import { OutlineItem } from '../components/wizard/useWizardStore';
import { ensureUids } from '../utils/outlineUtil';
import { extractBlobError } from '../utils/errorUtils';
import {
  supportsSavePicker,
  pickSaveLocation,
  writeBlobToFile,
  downloadBlob,
  WORD_FILE_TYPE,
} from '../utils/saveFile';
import type { PickedFileHandle } from '../utils/saveFile';
import { useExportProgress } from '../hooks/useExportProgress';
import ExportProgressModal from '../components/export/ExportProgressModal';
import { TemplateItem } from '../types';
import TipTapEditor from '../components/editor/TipTapEditor';
import { contentToBlocks } from '../utils/contentBlocks';
import PreviewToolbar from '../components/preview/PreviewToolbar';
import OutlineSidebar from '../components/preview/OutlineSidebar';
import StatusBar from '../components/preview/StatusBar';

const WordPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 状态
  const [outline, setOutline] = useState<OutlineItem[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templates, setTemplates] = useState<TemplateItem[]>([]);

  // 加载项目数据
  useEffect(() => {
    if (!id) return;

    // 加载项目数据
    api
      .get(`/api/projects/${id}`)
      .then((res) => {
        const raw = res.data.outline_data?.outline || [];
        setOutline(ensureUids(raw));
      })
      .catch(() => {
        toast.error('加载项目数据失败');
      });

    // 加载模板列表
    templateApi
      .getTemplates({ page_size: 50 })
      .then((res) => {
        setTemplates(res.templates || []);
      })
      .catch(() => {
        // 静默失败
      });
  }, [id]);

  // 刷新项目数据
  const handleRefresh = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await api.get(`/api/projects/${id}`);
      const raw = res.data.outline_data?.outline || [];
      setOutline(ensureUids(raw));
    } catch {
      toast.error('刷新项目数据失败');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // 模板切换
  const handleTemplateChange = useCallback(
    (templateId: number) => {
      setSelectedTemplateId(templateId);
      const found = templates.find((t) => t.id === templateId);
      setTemplateName(found ? found.template_name : '');
    },
    [templates]
  );

  const exportProgress = useExportProgress();

  // 导出下载
  const handleExport = useCallback(async () => {
    if (!id || !selectedTemplateId) return;

    // 先弹出系统"另存为"对话框，让用户选择保存位置（不支持的浏览器回退为直接下载）
    const filename = `标书文档_预览确认.docx`;
    let fileHandle: PickedFileHandle | null = null;
    if (supportsSavePicker()) {
      try {
        fileHandle = await pickSaveLocation(filename, WORD_FILE_TYPE);
        if (!fileHandle) return; // 用户取消
      } catch {
        fileHandle = null;
      }
    }

    try {
      exportProgress.start('word', '已提交导出请求，正在生成文档...');
      const response = await api.post(
        `/api/projects/${id}/export/word`,
        {
          use_template: true,
          template_id: selectedTemplateId,
        },
        { responseType: 'blob' }
      );

      exportProgress.setStage(fileHandle ? '文档生成完成，正在写入所选保存位置...' : '文档生成完成，正在准备下载...');
      const blob = new Blob([response.data], { type: WORD_FILE_TYPE.mimeType });
      if (fileHandle) {
        await writeBlobToFile(fileHandle, blob);
        exportProgress.finish(`已保存到所选位置：${fileHandle.name}`);
        toast.success(`文档已保存：${fileHandle.name}`);
      } else {
        downloadBlob(blob, filename);
        exportProgress.finish('下载已开始');
        toast.success('文档导出成功');
      }
    } catch (err: any) {
      exportProgress.cancel();
      const message = await extractBlobError(err, '导出失败，请重试');
      toast.error(message);
    }
  }, [id, selectedTemplateId, exportProgress]);

  // 展开目录树为扁平列表（包含所有层级的标题）
  const flatOutline = useMemo(() => {
    const result: OutlineItem[] = [];
    const walk = (items: OutlineItem[]) => {
      for (const item of items) {
        result.push(item);
        if (item.children && item.children.length > 0) {
          walk(item.children);
        }
      }
    };
    walk(outline);
    return result;
  }, [outline]);

  // 计算当前选中章节的 content blocks
  const activeBlocks = useMemo(() => {
    if (!activeChapterId) return [];
    const item = flatOutline.find((o) => o.uid === activeChapterId);
    return contentToBlocks(item?.content);
  }, [activeChapterId, flatOutline]);

  // block 操作（预览模式为空操作）
  const handleBlockAction = useCallback((_blockId: string, _action: string) => {
    // readOnly 模式不做任何操作
  }, []);

  // 目录点击
  const handleOutlineItemClick = useCallback((uid: string) => {
    setActiveChapterId(uid);
  }, []);

  // 返回向导
  const handleBack = useCallback(() => {
    navigate(`/projects/${id}/wizard`);
  }, [id, navigate]);

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]">
      {/* 顶部工具栏 */}
      <PreviewToolbar
        templates={templates}
        selectedTemplateId={selectedTemplateId}
        onTemplateChange={handleTemplateChange}
        onRefresh={handleRefresh}
        onExport={handleExport}
        onBack={handleBack}
        isLoading={isLoading}
      />

      {/* 主体：目录侧栏 + 文档预览 */}
      <div className="flex flex-1 overflow-hidden">
        {/* 左侧目录 */}
        <OutlineSidebar
          items={outline}
          activeId={activeChapterId}
          onItemClick={handleOutlineItemClick}
        />

        {/* 中间文档预览 */}
        <div className="flex-1 overflow-auto">
          <TipTapEditor
            blocks={activeBlocks}
            activeChapterId={activeChapterId}
            onBlockAction={handleBlockAction}
            isGenerating={false}
            readOnly={true}
          />
        </div>
      </div>

      {/* 底部状态栏 */}
      <StatusBar templateName={templateName} />
      <ExportProgressModal state={exportProgress.progressState} />
    </div>
  );
};

export default WordPreviewPage;
